import { FC, useState, useEffect } from "react";
import {
  APIProvider,
  AdvancedMarker,
  Map,
  InfoWindow,
  useAdvancedMarkerRef,
  useMap,
} from "@vis.gl/react-google-maps";
import { useNavigate } from "react-router-dom";
import { SiteResponse } from "../api/ApiClient";

const defaultProps = {
  center: {
    // sydney
    lat: -33.91277075825478,
    lng: 151.20202097822738,
  },
  zoom: 10,
};

const SiteMap: FC<{
  apiKey: string;
  sites: SiteResponse[];
  setSelectedSite: (id: string) => void;
}> = ({ apiKey, sites, setSelectedSite }) => {
  if (!apiKey) {
    return <div>Google Maps API key not available</div>;
  }

  return (
    <APIProvider apiKey={apiKey}>
      <SiteMapContent sites={sites} setSelectedSite={setSelectedSite} />
    </APIProvider>
  );
};

const SiteMapContent: FC<{
  sites: SiteResponse[];
  setSelectedSite: (id: string) => void;
}> = ({ sites, setSelectedSite }) => {
  const map = useMap();
  const navigate = useNavigate();
  const [center, setCenter] = useState(defaultProps.center);
  const [zoom, setZoom] = useState(defaultProps.zoom);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [selectedSite, setSelectedSiteState] = useState<SiteResponse | null>(null);

  useEffect(() => {
    if (!map || !sites || sites.length === 0) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();

    sites.forEach((site) => {
      if (
        site.latitude != null &&
        site.longitude != null &&
        site.latitude !== 0 &&
        site.longitude !== 0
      ) {
        bounds.extend(new google.maps.LatLng(site.latitude, site.longitude));
      }
    });

    // Fit map to bounds
    map.fitBounds(bounds);

    // Optional: Store center and zoom after fitBounds()
    google.maps.event.addListenerOnce(map, "idle", () => {
      const currentZoom = map.getZoom() || defaultProps.zoom;
      // Limit zoom between 1 and 15 for sanity (15 is street level, 20+ is too close)
      const adjustedZoom = Math.min(Math.max(currentZoom - 1, 1), 15);
      map.setZoom(adjustedZoom);

      setCenter({
        lat: map.getCenter()?.lat() || defaultProps.center.lat,
        lng: map.getCenter()?.lng() || defaultProps.center.lng,
      });
      setZoom(adjustedZoom);
    });
  }, [sites, map]);

  const handleSiteSelect = (site: SiteResponse) => {
    setSelectedSiteId(site.id || null);
    setSelectedSiteState(site);
    setSelectedSite(site.id || "");
  };

  const handleDrawerClose = () => {
    setSelectedSiteId(null);
    setSelectedSiteState(null);
    setSelectedSite("");
  };

  // Update selected site when parent component changes
  useEffect(() => {
    if (selectedSiteId && sites.length > 0) {
      const site = sites.find(s => s.id === selectedSiteId);
      if (site) {
        setSelectedSiteState(site);
      }
    }
  }, [selectedSiteId, sites]);

  return (
    <div style={{ 
      position: "relative", 
      width: "100%", 
      height: "600px",
      display: "flex"
    }}
    className="site-map-container">
      <style>
        {`
          @media (min-width: 992px) {
            .site-map-container {
              height: calc(100vh - 221px) !important; /* 100px footer + 121px header (79 + 42) */
            }
          }
        `}
      </style>
      {/* Left Drawer for site details */}
      <div 
        style={{
          position: "absolute",
          top: 0,
          left: selectedSite ? 0 : "-350px",
          width: "350px",
          height: "100%",
          backgroundColor: "white",
          boxShadow: "2px 0 12px rgba(0,0,0,0.15)",
          transition: "left 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          zIndex: 1000,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column"
        }}
      >
        {selectedSite && (
          <>
            {/* Header */}
            <div style={{ 
              padding: "20px", 
              borderBottom: "1px solid #e5e7eb",
              backgroundColor: "#f9fafb"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <h2 style={{ 
                  margin: 0, 
                  color: "#111827",
                  fontSize: "20px",
                  fontWeight: 600,
                  lineHeight: 1.3,
                  paddingRight: "8px"
                }}>
                  {selectedSite.name}
                </h2>
                <button 
                  onClick={handleDrawerClose}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "28px",
                    cursor: "pointer",
                    color: "#6b7280",
                    padding: 0,
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "4px",
                    transition: "background-color 0.2s"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e5e7eb"}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                >
                  &times;
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div style={{ padding: "20px", flex: 1 }}>
              {/* Address */}
              {selectedSite.address && (
                <div style={{ 
                  display: "flex", 
                  alignItems: "flex-start", 
                  marginBottom: "12px",
                  padding: "12px",
                  backgroundColor: "#f3f4f6",
                  borderRadius: "8px"
                }}>
                  <span style={{ marginRight: "10px", fontSize: "18px" }}>📍</span>
                  <span style={{ color: "#374151", fontSize: "14px", lineHeight: 1.5 }}>
                    {selectedSite.address}
                  </span>
                </div>
              )}

              {/* Access Type Badges */}
              {selectedSite.accessTypes && selectedSite.accessTypes.length > 0 && (
                <div style={{ 
                  display: "flex", 
                  flexWrap: "wrap", 
                  gap: "8px", 
                  marginBottom: "20px" 
                }}>
                  {selectedSite.accessTypes.map((accessType, index) => (
                    <span
                      key={index}
                      style={{
                        padding: "4px 12px",
                        borderRadius: "9999px",
                        fontSize: "12px",
                        fontWeight: 600,
                        textTransform: "capitalize",
                        backgroundColor: accessType === "permanent" ? "#fff7ed" : "#f0fdf4",
                        color: accessType === "permanent" ? "#c2410c" : "#15803d",
                        border: `1px solid ${accessType === "permanent" ? "#fdba74" : "#86efac"}`
                      }}
                    >
                      {accessType === "permanent" ? "Permanent" : accessType}
                    </span>
                  ))}
                </div>
              )}
              
              {/* Availability */}
              <div style={{ 
                marginBottom: "20px",
                padding: "16px",
                backgroundColor: selectedSite.currentCapacity && selectedSite.currentCapacity > 0 
                  ? "#ecfdf5" 
                  : "#fef2f2",
                borderRadius: "8px",
                border: `2px solid ${selectedSite.currentCapacity && selectedSite.currentCapacity > 0 
                  ? "#10b981" 
                  : "#ef4444"}`
              }}>
                <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "4px", fontWeight: 500 }}>
                  Availability
                </div>
                <div style={{ 
                  color: selectedSite.currentCapacity && selectedSite.currentCapacity > 0 
                    ? '#059669' 
                    : '#dc2626',
                  fontWeight: 700,
                  fontSize: "24px"
                }}>
                  {selectedSite.currentCapacity !== undefined 
                    ? `${selectedSite.currentCapacity}` 
                    : 'N/A'}
                </div>
                <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "2px" }}>
                  spaces available
                </div>
              </div>
              
              {/* Additional Details */}
              <div style={{ marginBottom: "20px" }}>
                {selectedSite.bayTypes && selectedSite.bayTypes.length > 0 && (
                  <div style={{ 
                    marginBottom: "12px",
                    paddingBottom: "12px",
                    borderBottom: "1px solid #e5e7eb"
                  }}>
                    <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "4px", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.5px" }}>
                      Bay Types
                    </div>
                    <ul style={{ 
                      margin: 0, 
                      paddingLeft: "20px", 
                      color: '#111827', 
                      fontSize: "14px" 
                    }}>
                      {selectedSite.bayTypes.map((bayType, index) => (
                        <li key={index} style={{ marginBottom: "4px" }}>{bayType}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {selectedSite.city && (
                  <div style={{ 
                    marginBottom: "12px",
                    paddingBottom: "12px",
                    borderBottom: "1px solid #e5e7eb"
                  }}>
                    <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "4px", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.5px" }}>
                      City
                    </div>
                    <div style={{ color: '#111827', fontSize: "14px" }}>
                      {selectedSite.city}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Action Button */}
              <button
                className="orangeButton"
                style={{
                  width: "100%",
                }}
                onClick={() => navigate(`/reservations/00000000-0000-0000-0000-000000000000?siteId=${selectedSite.id}`)}
              >
                Book Parking
              </button>
            </div>
          </>
        )}
      </div>

      {/* Map */}
      <div style={{ 
        flex: 1, 
        marginLeft: selectedSite ? "350px" : "0",
        transition: "margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        width: "100%",
        height: "100%"
      }}>
        <Map
          mapId="site-map"
          style={{ width: "100%", height: "100%" }}
          defaultCenter={center}
          defaultZoom={zoom}
        >
          {sites.map((site) => (
            <Site
              key={site.id}
              site={site}
              onClick={() => handleSiteSelect(site)}
              isSelected={selectedSiteId === site.id}
            />
          ))}
        </Map>
      </div>
    </div>
  );
};

const Site: FC<{ site: SiteResponse; onClick: () => void; isSelected: boolean }> = ({
  site,
  onClick,
  isSelected,
}) => {
  const [markerRef, marker] = useAdvancedMarkerRef();
  const [hovering, setHovering] = useState(false);
  const hasPermanentAccess = site.accessTypes?.includes("permanent") ?? false;

  const handleMarkerClick = () => {
    onClick();
  };

  return (
    <>
      <AdvancedMarker
        ref={markerRef}
        position={{
          lat: site.latitude || 0,
          lng: site.longitude || 0,
        }}
        onClick={handleMarkerClick}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      >
        <div style={{
          transform: isSelected ? "scale(1.2)" : hovering ? "scale(1.1)" : "scale(1)",
          transition: "transform 0.2s ease",
          filter: isSelected ? "drop-shadow(0 0 8px rgba(13, 110, 253, 0.6))" : "none",
          borderRadius: "50%",
          padding: hasPermanentAccess ? "5px" : "0",
          border: hasPermanentAccess ? "3px solid #fe9015" : "none",
          backgroundColor: hasPermanentAccess ? "#fe9015" : "transparent"
        }}>
          <img
            src="/images/shareparkSquareLogoMapIcon.svg"
            alt="site"
            style={{ 
              height: "40px", 
              width: "40px",
              cursor: "pointer",
              display: "block"
            }}
          />
        </div>
      </AdvancedMarker>
      
      {/* Simple tooltip on hover - just name */}
      {hovering && !isSelected && (
        <InfoWindow
          anchor={marker}
          onCloseClick={() => setHovering(false)}
        >
          <div style={{ 
            padding: "4px 8px",
            minWidth: "120px"
          }}>
            <strong style={{ fontSize: "14px", color: "#111827" }}>
              {site.name}
            </strong>
          </div>
        </InfoWindow>
      )}
    </>
  );
};

export default SiteMap;